import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Star, Quote, ExternalLink } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const GOOGLE_REVIEW_URL =
  "https://www.google.com/maps/place/Locker+Room/@25.534569,34.6375349,17z/data=!3m1!4b1!4m6!3m5!1s0x15b4a9a3935653ab:0x4df3f7f1b1055958!8m2!3d25.534569!4d34.6375349!16s%2Fg%2F11nbzxr0zy";


const reviews = [
  {
    name: "Petr Danda",
    meta: "Local Guide · 10 reviews",
    text: "Great store, perfect jerseys, great selection. Perfect approach, great dealings, thank you very much for a pleasant experience. And also for the great jersey. Nice dealings with the store owner.",
  },
  {
    name: "Hannes Schauer",
    meta: "6 reviews",
    text: "Sehr netter Italiener. Tolle Qualität für einen angemessenen Preis. Meine Söhne hatten wirklich Freude damit. Absolute Kaufempfehlung.",
  },
  {
    name: "Majus M",
    meta: "8 reviews",
    text: "Very good quality, shipped it to Lithuania it arrived quite fast. Recommended this shop. Best one.",
  },
  {
    name: "Bruno Kijek",
    meta: "4 reviews",
    text: "The best of the best. Amazing seller and quality.",
  },
];

export const ReviewsSection = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section
      id="reviews"
      className="py-24 md:py-32 bg-[#050505] relative"
      ref={ref}
      data-testid="reviews-section"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="section-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#009A49] mb-6 font-body">
            {t("reviews.label")}
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-wide">
            {t("reviews.title")}
          </h2>
          <div className="emerald-divider mt-8" />
        </motion.div>

        {/* Reviews grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {reviews.map((r, index) => (
            <motion.div
              key={r.name}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative p-8 border border-white/5 rounded-sm bg-[#0A0A0A] hover:border-[#009A49]/30 transition-colors duration-300"
              data-testid={`review-${index}`}
            >
              <Quote className="absolute top-6 right-6 w-8 h-8 text-[#009A49]/20" />

              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-[#009A49] fill-[#009A49]" />
                ))}
              </div>

              {/* Text */}
              <p className="text-zinc-300 font-body text-base leading-relaxed mb-6">
                &ldquo;{r.text}&rdquo;
              </p>

              {/* Author */}
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#009A49]/15 border border-[#009A49]/30 flex items-center justify-center">
                  <span className="font-heading text-lg text-[#009A49]">{r.name.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-heading text-white text-base tracking-wide">{r.name}</p>
                  <p className="text-zinc-600 font-body text-xs">{r.meta}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Leave a review CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 flex justify-center"
        >
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 border border-[#009A49] rounded-full text-[#009A49] hover:bg-[#009A49] hover:text-white font-body text-sm font-semibold tracking-wider uppercase transition-colors duration-300"
            data-testid="leave-review-btn"
          >
            <Star className="w-4 h-4 fill-current" />
            {t("reviews.leaveReview")}
            <ExternalLink className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
